/* eslint-disable react/jsx-indent */
import { useUser } from '@folklore/auth';
import classNames from 'classnames';
import queryString from 'query-string';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useSearch } from 'wouter';

import * as AppPropTypes from '../../lib/PropTypes';

import { useThemeClassName } from '../../contexts/ThemeContext';
import ProfileButton from '../buttons/ProfileButton';
import StyledButton from '../buttons/StyledButton';
import Leaderboard from '../partials/Leaderboard';
import PageTitle from '../partials/PageTitle';

import styles from '../../../styles/pages/leaderboard.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
};

function LeaderboardTabs({ page }) {
    const themeClassName = useThemeClassName(styles);
    const user = useUser();
    const { title: pageTitle = 'Page', description = null, tables = null } = page;
    const hasTabs = tables !== null && tables.length > 1;
    const [firstTable = null] = tables || [];

    const [pathname, navigate] = useLocation();
    const search = useSearch();
    const { tab = null } = useMemo(() => queryString.parse(search) || {}, [search]);

    const table =
        tables !== null && tab !== null
            ? tables.find(({ id }) => `${id}` === `${tab}`) || firstTable
            : firstTable;
    const { id: tableId = null, title: tableTitle, items: tableItems } = table || {};

    const onTabClick = useCallback(
        (id) => {
            navigate(`${pathname}?tab=${id}`);
        },
        [pathname],
    );

    return (
        <div className={classNames([styles.container, themeClassName])}>
            <div className={styles.content}>
                <PageTitle title={pageTitle} className={styles.title} />
                {description !== null ? <p className={styles.description}>{description}</p> : null}
                {hasTabs ? (
                    <div className={styles.tabsContainer}>
                        <div className={styles.tabs}>
                            {(tables || []).map(({ id = null, title = null }, drawIndex) => (
                                <StyledButton
                                    key={`draw-${drawIndex + 1}`}
                                    className={classNames([
                                        styles.tab,
                                        {
                                            [styles.active]: `${id}` === `${tableId}`,
                                        },
                                    ])}
                                    onClick={() => {
                                        onTabClick(id);
                                    }}
                                >
                                    {title || null}
                                </StyledButton>
                            ))}
                        </div>
                    </div>
                ) : null}

                <div className={styles.profileLinkContainer}>
                    <ProfileButton user={user} />
                </div>

                <div className={styles.tables}>
                    {firstTable !== null ? (
                        <div className={styles.table} key={`table-${tableId}`}>
                            {tableTitle !== null ? (
                                <h3 className={styles.tableTitle}>{tableTitle}</h3>
                            ) : null}
                            <Leaderboard
                                columns={null}
                                items={tableItems || []}
                                className={styles.leaderboard}
                            />
                        </div>
                    ) : null}
                </div>

                {tables === null || tables.length === 0 ? (
                    <div className={styles.noLeaders}>
                        <FormattedMessage
                            defaultMessage="No participants yet."
                            description="Leaderboard page"
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
}

LeaderboardTabs.propTypes = propTypes;

export default LeaderboardTabs;
